import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getProducts } from "../../utils/storage";

// ── SEARCH LOGIC ──────────────────────────────────────────────
function searchProducts(query) {
  if (!query || query.trim() === "") return [];
  const q = query.toLowerCase().trim();
  return getProducts().filter(
    (p) =>
      p.isActive !== false &&
      (p.name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q))
  );
}

// ── PRODUCT CARD (unchanged) ──────────────────────────────────
function ProductCard({ item, index, onClick }) {
  const image = item.images?.[0] || null;
  const cardRef = useRef(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add("sp-card-visible"), index * 70);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [index]);

  return (
    <div ref={cardRef} className="sp-card" onClick={onClick}>
      <div className="sp-badge">{item.badge || "Product"}</div>

      <div className="sp-card-actions">
        <button
          className="sp-action-btn"
          title="View product"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
          <i className="ri-eye-line" />
        </button>
      </div>

      <div className="sp-img-wrap">
        {image
          ? <img src={image} alt={item.name} />
          : <div className="sp-img-placeholder">📦</div>
        }
      </div>

      <div className="sp-card-body">
        <div className="sp-brand">{item.brand}</div>
        <div className="sp-title">{item.name}</div>
        {item.shortDescription && (
          <div className="sp-desc">{item.shortDescription}</div>
        )}
        <div className="sp-stock">
          <div className="sp-stock-dot" />
          {item.availability || "In Stock"} · Fast Shipping
        </div>
      </div>

      <div className="sp-card-footer">
        <button
          className="sp-cta"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
        >
          <i className="ri-eye-line" />
          View Product
        </button>
      </div>
    </div>
  );
}

// ── EMPTY STATE (unchanged) ───────────────────────────────────
function EmptyState({ query }) {
  return (
    <div className="sp-empty">
      <div className="sp-empty-icon">
        <i className="ri-search-line" />
      </div>
      <div className="sp-empty-title">No results found</div>
      <div className="sp-empty-sub">
        We couldn't find anything matching{" "}
        <span className="sp-empty-q">"{query}"</span>.
        Try a different product name or brand.
      </div>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────
export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [results, setResults] = useState([]);
  const [sortBy, setSortBy] = useState("relevance");

  useEffect(() => {
    window.scrollTo(0, 0);
    setResults(searchProducts(query));
    setSortBy("relevance");
  }, [query]);

  const sortedResults = [...results].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "brand") return a.brand.localeCompare(b.brand);
    return 0;
  });

  const handleProductClick = (product) => {
    navigate(`/category/${product.categorySlug}/${product.id}`);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        @import url('https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css');

        :root {
          --sp-dark:       #1B211A;
          --sp-green:      #628141;
          --sp-green-soft: #8BAE66;
          --sp-bg:         #eef2eb;
          --sp-white:      #ffffff;
          --sp-border:     rgba(0,0,0,0.07);
        }

        .sp-page {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          background: var(--sp-bg);
          padding: 0 0 80px;
        }

        /* ── HERO ── */
        .sp-hero {
          padding: 48px 8% 32px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }
        .sp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: .15em;
          text-transform: uppercase;
          color: var(--sp-green);
          margin-bottom: 10px;
        }
        .sp-eyebrow-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: var(--sp-green);
        }
        .sp-hero h1 {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: clamp(2rem, 4.5vw, 3rem);
          font-weight: 800;
          color: var(--sp-dark);
          line-height: 1.04;
          letter-spacing: -.02em;
          margin: 0 0 8px;
        }
        .sp-hero h1 em {
          color: var(--sp-green);
          font-style: normal;
        }
        .sp-hero-sub {
          font-size: 14px;
          color: #7c8c7c;
          line-height: 1.65;
          max-width: 400px;
          margin: 0;
        }
        .sp-btn-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 20px;
          border: 1.5px solid rgba(0,0,0,0.1);
          border-radius: 100px;
          background: var(--sp-white);
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #444;
          cursor: pointer;
          transition: all .2s;
          min-height: 40px;
          flex-shrink: 0;
          align-self: flex-start;
          margin-top: 6px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .sp-btn-back:hover {
          border-color: var(--sp-green);
          color: var(--sp-green);
          box-shadow: 0 2px 10px rgba(98,129,65,0.15);
        }

        /* ── DIVIDER ROW ── */
        .sp-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding: 0 8% 24px;
        }
        .sp-count {
          font-size: 13px;
          font-weight: 600;
          color: var(--sp-green);
        }
        .sp-sort {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .sp-sort-label {
          font-size: 12px;
          color: #aaa;
        }
        .sp-sort-pills { display: flex; gap: 5px; }
        .sp-sort-pill {
          font-size: 12px;
          font-weight: 600;
          padding: 5px 14px;
          border-radius: 100px;
          border: 1.5px solid rgba(0,0,0,0.1);
          background: rgba(255,255,255,0.6);
          color: #666;
          cursor: pointer;
          transition: all .18s;
          min-height: 30px;
          display: inline-flex;
          align-items: center;
        }
        .sp-sort-pill:hover {
          border-color: var(--sp-green);
          color: var(--sp-green);
        }
        .sp-sort-pill.active {
          background: var(--sp-dark);
          color: #fff;
          border-color: var(--sp-dark);
        }

        /* ── GRID ── */
        .sp-grid-wrap {
          max-width: 1360px;
          margin: 0 auto;
          padding: 0 8% 60px;
        }
        .sp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 22px;
        }

        /* ── CARD (unchanged) ── */
        .sp-card {
          border-radius: 20px;
          overflow: hidden;
          background: linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%);
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: 0 4px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
          display: flex;
          flex-direction: column;
          cursor: pointer;
          position: relative;
          opacity: 0;
          transform: translateY(28px);
          transition:
            transform 0.35s cubic-bezier(0.4,0,0.2,1),
            box-shadow 0.35s cubic-bezier(0.4,0,0.2,1),
            border-color 0.25s,
            opacity 0.4s ease;
        }
        .sp-card.sp-card-visible { opacity: 1; transform: translateY(0); }
        .sp-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 0 0 1.5px rgba(98,129,65,0.35);
          border-color: rgba(98,129,65,0.35);
        }
        .sp-badge {
          position: absolute;
          top: 14px; left: 14px; z-index: 3;
          font-size: 10px; font-weight: 700;
          letter-spacing: .08em; text-transform: uppercase;
          color: #fff; padding: 4px 12px; border-radius: 100px;
          background: linear-gradient(135deg, var(--sp-green), var(--sp-green-soft));
          box-shadow: 0 3px 10px rgba(98,129,65,0.35);
        }
        .sp-card-actions {
          position: absolute;
          top: 14px; right: 14px; z-index: 3;
          opacity: 0; transform: translateX(10px);
          transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
        }
        .sp-card:hover .sp-card-actions { opacity: 1; transform: translateX(0); }
        .sp-action-btn {
          width: 34px; height: 34px; border-radius: 10px;
          border: 1.5px solid rgba(0,0,0,0.08);
          background: rgba(255,255,255,0.92);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          font-size: 15px; color: #444; cursor: pointer;
          transition: all .18s;
        }
        .sp-action-btn:hover {
          border-color: var(--sp-green); color: var(--sp-green);
          background: rgba(98,129,65,0.08); transform: scale(1.1);
        }
        .sp-img-wrap {
          height: 200px;
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          background: radial-gradient(circle at 50% 30%, rgba(98,129,65,0.12) 0%, rgba(245,245,245,0.7) 70%, transparent 100%);
          position: relative; overflow: hidden;
        }
        .sp-img-wrap::after {
          content: ''; position: absolute;
          bottom: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(98,129,65,0.18), transparent);
        }
        .sp-img-wrap img {
          max-height: 145px; max-width: 100%; object-fit: contain;
          filter: drop-shadow(0 8px 20px rgba(0,0,0,0.10));
          transition: transform 0.45s cubic-bezier(0.4,0,0.2,1);
        }
        .sp-card:hover .sp-img-wrap img {
          transform: scale(1.1) rotate(-1deg);
          filter: drop-shadow(0 14px 28px rgba(98,129,65,0.18));
        }
        .sp-img-placeholder { font-size: 52px; opacity: .3; }
        .sp-card-body {
          padding: 18px 18px 0; flex: 1;
          display: flex; flex-direction: column; gap: 6px;
        }
        .sp-brand {
          font-size: 10.5px; font-weight: 700;
          letter-spacing: .1em; text-transform: uppercase;
          color: var(--sp-green);
        }
        .sp-title {
          font-size: 14px; font-weight: 600; color: #111;
          line-height: 1.45;
          display: -webkit-box; -webkit-line-clamp: 2;
          -webkit-box-orient: vertical; overflow: hidden; flex: 1;
        }
        .sp-desc {
          font-size: 12px; color: #888; line-height: 1.5;
          display: -webkit-box; -webkit-line-clamp: 2;
          -webkit-box-orient: vertical; overflow: hidden;
        }
        .sp-stock {
          display: flex; align-items: center; gap: 5px;
          font-size: 11.5px; font-weight: 600;
          color: var(--sp-green); padding-bottom: 4px;
        }
        .sp-stock-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: var(--sp-green);
          box-shadow: 0 0 6px rgba(98,129,65,0.5);
        }
        .sp-card-footer { padding: 12px 18px 18px; }
        .sp-cta {
          width: 100%; padding: 11px; border: none;
          border-radius: 12px; background: #111; color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          gap: 8px; min-height: 44px; letter-spacing: .02em;
          opacity: 0; transform: translateY(8px);
          transition:
            opacity 0.28s cubic-bezier(0.4,0,0.2,1),
            transform 0.28s cubic-bezier(0.4,0,0.2,1),
            background 0.2s;
        }
        .sp-card:hover .sp-cta { opacity: 1; transform: translateY(0); }
        .sp-cta:hover { background: var(--sp-green); }

        /* ── EMPTY ── */
        .sp-empty {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          min-height: 50vh; gap: 14px;
          text-align: center; padding: 0 24px;
        }
        .sp-empty-icon {
          width: 80px; height: 80px; border-radius: 50%;
          background: rgba(98,129,65,0.08);
          border: 1.5px solid rgba(98,129,65,0.15);
          display: flex; align-items: center; justify-content: center;
          font-size: 34px; color: var(--sp-green-soft);
          margin-bottom: 4px;
        }
        .sp-empty-title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 26px; font-weight: 800;
          color: var(--sp-dark); letter-spacing: -.02em;
        }
        .sp-empty-sub { font-size: 14px; color: #6b7280; max-width: 300px; line-height: 1.7; }
        .sp-empty-q { color: var(--sp-green); font-weight: 700; }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .sp-hero { padding: 36px 6% 24px; }
          .sp-meta { padding: 0 6% 20px; }
          .sp-grid-wrap { padding: 0 6% 60px; }
        }
        @media (max-width: 640px) {
          .sp-hero { padding: 28px 5% 20px; }
          .sp-meta { padding: 0 5% 16px; }
          .sp-grid-wrap { padding: 0 5% 60px; }
          .sp-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
          .sp-cta { opacity: 1; transform: translateY(0); }
          .sp-card-actions { opacity: 1; transform: translateX(0); }
          .sp-sort-label { display: none; }
        }
        @media (max-width: 480px) {
          .sp-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
          .sp-card { border-radius: 14px; }
          .sp-img-wrap { height: 140px; padding: 14px; }
          .sp-card-body { padding: 12px 12px 0; }
          .sp-card-footer { padding: 10px 12px 14px; }
        }
      `}</style>

      <div className="sp-page">

        {/* ── HERO ── */}
        <div className="sp-hero">
          <div>
            <div className="sp-eyebrow">
              <div className="sp-eyebrow-dot" />
              Search Results
            </div>
            <h1>
              {query
                ? <>Results for <em>"{query}"</em></>
                : <>Search <em>Products</em></>
              }
            </h1>
            <p className="sp-hero-sub">
              {query
                ? "Showing products matching your search across all categories."
                : "Enter a product name or brand to find what you need."
              }
            </p>
          </div>
          <button className="sp-btn-back" onClick={() => navigate("/")}>
            <i className="ri-arrow-left-line" />
            Back
          </button>
        </div>

        {/* ── META ROW ── */}
        {results.length > 0 && (
          <div className="sp-meta">
            <span className="sp-count">
              {results.length} product{results.length !== 1 ? "s" : ""} found
            </span>
            <div className="sp-sort">
              <span className="sp-sort-label">Sort by</span>
              <div className="sp-sort-pills">
                {["relevance", "name", "brand"].map((opt) => (
                  <button
                    key={opt}
                    className={`sp-sort-pill${sortBy === opt ? " active" : ""}`}
                    onClick={() => setSortBy(opt)}
                  >
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {query === "" || results.length === 0 ? (
          <EmptyState query={query || "..."} />
        ) : (
          <div className="sp-grid-wrap">
            <div className="sp-grid">
              {sortedResults.map((product, i) => (
                <ProductCard
                  key={product.id}
                  item={product}
                  index={i}
                  onClick={() => handleProductClick(product)}
                />
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
}